import { ROOT_PATH } from "../api/Properties";
import useSWR from "swr";
import useAuthenticatedAxios from "./use-authenticated-axios";
import { useMemo } from "react";
import useSWRImmutable from "swr/immutable";
import useResources from "./use-resources";
import { findAndUpdate } from "../utils/index.jsx";

import useAuth from "./use-auth.js";
import useProfileFilter from "./use-profile-filter";
import { naturalCompare } from "../utils/sort";

export function usePublicProperties(options = {}) {
  const { withById = false } = options;
  const {
    resources: publicProperties,
    resourcesById: publicPropertiesById,
    error,
    isLoading,
  } = useResources("/biens/public", {}, { withById });
  return {
    publicProperties,
    publicPropertiesById,
    error,
    isLoading,
  };
}

export function usePublicProperty(propertyId) {
  const axios = useAuthenticatedAxios();
  const {
    data: property,
    error,
    isLoading,
  } = useSWRImmutable(
    propertyId ? `/biens/${propertyId}/public` : null,
    (url) => {
      return axios.get(url).then((res) => res.data);
    },
  );
  return {
    property,
    error,
    isLoading,
  };
}
export function useAllProperties() {
  const axios = useAuthenticatedAxios();
  const {
    data: properties,
    error,
    isLoading,
  } = useSWRImmutable("/biens/admin", (url) => {
    return axios.get(url).then((res) => res.data);
  });
  return {
    properties,
    error,
    isLoading,
  };
}

export default function useProperties(options = {}) {
  const { noFetch = false, params = {}, withById = false } = options;
  const axios = useAuthenticatedAxios();
  const { auth } = useAuth();
  const { matchesProfileFilter } = useProfileFilter();
  const fallbackData = useMemo(() => [], []);

  const {
    data: allProperties,
    error,
    isLoading,
    mutate,
  } = useSWR(
    // Seules les options qui changent la réponse font partie de la clé : un
    // cache par variante de `withById` donnerait des copies divergentes du
    // même bien, et les formulaires renverraient la copie périmée au serveur.
    [ROOT_PATH, auth.userId, { noFetch, params }],
    ([url]) => {
      if (noFetch) return Promise.resolve([]);
      return axios
        .get(url, {
          params,
          paramsSerializer: {
            indexes: null, // no brackets at all
          },
        })
        .then((res) => res.data);
    },
    { fallbackData },
  );

  const properties = useMemo(
    () =>
      allProperties
        .filter((property) =>
          matchesProfileFilter(property.id_profile_nom_owner),
        )
        .sort((p1, p2) => naturalCompare(p1.nom, p2.nom)),
    [allProperties, matchesProfileFilter],
  );

  function addProperty(property) {
    let newProperty;
    return mutate(
      async (currentProperties) => {
        const response = await axios.post(ROOT_PATH, property);
        newProperty = response.data;
        if (property.is_immeuble) {
          return currentProperties;
        }
        return [...currentProperties, response.data];
      },
      { revalidate: true },
    ).then(() => newProperty);
  }

  function duplicateProperty(property) {
    return mutate(
      async (currentProperties) => {
        const response = await axios.post(
          `${ROOT_PATH}/${property.id}/duplicate`,
        );
        return [...currentProperties, response.data];
      },
      { revalidate: false },
    );
  }

  function deleteProperty(propertyId) {
    return mutate(
      async (currentProperties) => {
        await axios.delete(`${ROOT_PATH}/${propertyId}`);

        return currentProperties.filter(
          (property) => property.id !== propertyId,
        );
      },
      { revalidate: false },
    );
  }

  function replaceProperty(currentProperties, propertyId, property) {
    const propertyIndex = currentProperties.findIndex(
      (current) => current.id === propertyId,
    );
    if (propertyIndex === -1) {
      throw new Error("Bien non trouvé");
    }
    const newProperties = [...currentProperties];
    newProperties[propertyIndex] = property;
    return newProperties;
  }

  function updateProperty(propertyId, property) {
    return mutate(
      async (currentProperties) => {
        const response = await axios.put(
          `${ROOT_PATH}/${propertyId}`,
          property,
        );
        return replaceProperty(currentProperties, propertyId, response.data);
      },
      { revalidate: false },
    );
  }

  function updatePropertyCles(propertyId, cles) {
    return mutate(
      async (currentProperties) => {
        const response = await axios.put(
          `${ROOT_PATH}/${propertyId}/cles`,
          cles,
        );
        return replaceProperty(currentProperties, propertyId, response.data);
      },
      { revalidate: false },
    );
  }

  const propertiesById = useMemo(() => {
    if (!withById) return {};
    return properties.reduce((acc, property) => {
      acc[property.id] = property;
      return acc;
    }, {});
  }, [properties, withById]);

  async function propertyToQlower(propertyId) {
    let newResource;
    await mutate(
      async (current) => {
        const response = await axios.put(
          `${ROOT_PATH}/${propertyId}/send-to-compta`,
        );
        newResource = response.data;
        return findAndUpdate(
          current,
          (resource) => resource.id === propertyId,
          newResource,
        );
      },
      { revalidate: false },
    );
    return newResource;
  }
  async function propertyTransacToQlower(propertyId, year) {
    let newResource;
    await mutate(
      async (current) => {
        const response = await axios.put(
          `${ROOT_PATH}/${propertyId}/send-transac-to-compta`,
          { year },
        );
        newResource = response.data;
        return findAndUpdate(
          current,
          (resource) => resource.id === propertyId,
          newResource,
        );
      },
      { revalidate: false },
    );
    return newResource;
  }

  return {
    properties,
    propertiesById,
    error,
    isLoading,
    addProperty,
    updateProperty,
    updatePropertyCles,
    deleteProperty,
    duplicateProperty,
    propertyToQlower,
    propertyTransacToQlower,
  };
}
